import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@src/hooks/useDebounce";
import { useQueryData } from "@src/hooks/useQueryData";

type IdValue = string | number;

const SHOW_ALL_QUERY_KEY = "__all__";

export type IsValueIdFn<T, Id> = (value: T | Id) => value is Id;

function defaultIsValueId<T, Id>(value: T | Id): value is Id {
    if (value == null) return false;
    return typeof value === "string" || typeof value === "number";
}

export type UseBusinessSearchSelectParams<T, Id> = {
    value: T | Id | null;
    minSearchLength?: number;
    debounceDelay?: number;
    queryKeyBase: readonly unknown[];
    loadOptions: (query: string, signal?: AbortSignal) => Promise<T[]>;
    loadById: (id: Id, signal?: AbortSignal) => Promise<T | null>;
    getId: (entity: T) => Id;
    getOptionLabel: (entity: T) => string;
    getSelectedLabel?: (entity: T) => string;
    transformOptions?: (items: T[]) => T[];
    onChange?: (selected: T | null) => void;
    disabled?: boolean;
    isValueId?: IsValueIdFn<T, Id>;
    isIdEqual?: (a: Id, b: Id) => boolean;
    byIdQueryEnabled?: boolean;
    byIdStaleTime?: number;
};

export type UseBusinessSearchSelectResult<T, Id extends IdValue | object = IdValue> = {
    options: T[] | null;
    resolvedValue: T | null;
    isResolving: boolean;
    isUnresolved: boolean;
    unresolvedValueId: Id | null;
    searchQuery: string;
    setSearchQuery: (next: string) => void;
    handleSearch: (query: string) => void;
    /** Явно запросить загрузку полного списка (обход `minSearchLength`). */
    handleShowAll: () => void;
    /**
     * Любой fetch поиска или by-id (legacy-агрегат). \
     * Для overlay UI предпочитайте {@link isSearchLoading} / {@link isSearchFetching}.
     */
    isLoading: boolean;
    /**
     * Первичная загрузка поискового запроса (ещё нет `data`). \
     * Для blocking `SearchSelect.isLoading`.
     */
    isSearchLoading: boolean;
    /**
     * Любой fetch поискового запроса, включая refetch при уже известных `data`. \
     * Для soft refresh: `isSearchFetching && !isSearchLoading` → `SearchSelect.isRefreshing`.
     */
    isSearchFetching: boolean;
    idle: boolean;
    handleChange: (selected: T | null) => void;
    getOptionLabel: (entity: T) => string;
    getSelectedLabel: (entity: T) => string;
};

/**
 * Debounced search + resolve by id для {@link BusinessSearchSelect}.
 */
export function useBusinessSearchSelect<T extends object, Id extends IdValue | object>(
    params: UseBusinessSearchSelectParams<T, Id>,
): UseBusinessSearchSelectResult<T, Id> {
    const {
        value,
        minSearchLength = 2,
        debounceDelay = 500,
        queryKeyBase,
        loadOptions,
        loadById,
        getId,
        getOptionLabel,
        getSelectedLabel,
        transformOptions,
        onChange,
        disabled = false,
        isValueId = defaultIsValueId as IsValueIdFn<T, Id>,
        isIdEqual = (a, b) => a === b,
        byIdQueryEnabled = true,
        byIdStaleTime,
    } = params;

    const [searchQuery, setSearchQuery] = useState("");
    const [showAllRequested, setShowAllRequested] = useState(false);
    const debouncedSearchQuery = useDebounce(searchQuery, debounceDelay);

    const valueIsId = value != null && isValueId(value);
    const valueAsEntity = value != null && !valueIsId ? value : null;
    const valueAsId = valueIsId ? value : null;

    const searchQueryEnabled = !disabled && (showAllRequested || debouncedSearchQuery.length >= minSearchLength);

    const searchQueryKey = showAllRequested ? SHOW_ALL_QUERY_KEY : debouncedSearchQuery;

    const searchQueryResult = useQueryData({
        queryKey: [...queryKeyBase, "search", searchQueryKey],
        queryFn: async ({ signal }) => {
            if (!showAllRequested && debouncedSearchQuery.length < minSearchLength) return [];
            const queryToLoad = showAllRequested ? "" : debouncedSearchQuery;
            return loadOptions(queryToLoad, signal);
        },
        enabled: searchQueryEnabled,
    });

    const byIdQuery = useQueryData({
        queryKey: [...queryKeyBase, "byId", valueAsId],
        queryFn: async ({ signal }) => {
            if (valueAsId == null) return null;
            return loadById(valueAsId, signal);
        },
        enabled: valueAsId != null && byIdQueryEnabled,
        ...(byIdStaleTime != null ? { staleTime: byIdStaleTime } : {}),
    });

    const options = useMemo(() => {
        if (!searchQueryEnabled) return null;
        const raw = searchQueryResult.data ?? [];
        return transformOptions != null ? transformOptions(raw) : raw;
    }, [searchQueryEnabled, searchQueryResult.data, transformOptions]);

    const resolvedValue = useMemo((): T | null => {
        if (value == null) return null;
        if (valueAsEntity != null) return valueAsEntity;
        if (valueAsId == null) return null;
        const loaded = byIdQuery.data;
        if (loaded != null && isIdEqual(getId(loaded), valueAsId)) return loaded;
        return null;
    }, [value, valueAsEntity, valueAsId, byIdQuery.data, getId, isIdEqual]);

    const isResolving = valueAsId != null && resolvedValue == null && byIdQuery.isFetching;
    const isUnresolved =
        valueAsId != null && !byIdQuery.isFetching && resolvedValue == null && (byIdQuery.isSuccess || byIdQuery.isError);
    const unresolvedValueId = isResolving || isUnresolved ? valueAsId : null;

    const handleSearch = (query: string) => {
        setShowAllRequested(false);
        setSearchQuery(query);
    };

    const handleShowAll = useCallback(() => {
        setShowAllRequested(true);
    }, []);

    const handleChange = useCallback(
        (selected: T | null) => {
            if (selected != null) {
                byIdQuery.setDataForKey<T>([...queryKeyBase, "byId", getId(selected)], selected);
            }
            onChange?.(selected);
            if (selected != null) {
                setSearchQuery("");
                setShowAllRequested(false);
            }
        },
        [getId, onChange, queryKeyBase, byIdQuery],
    );

    const getSelectedLabelResolved = getSelectedLabel ?? getOptionLabel;
    const idle = !searchQueryEnabled;

    return {
        options,
        resolvedValue,
        isResolving,
        isUnresolved,
        unresolvedValueId,
        searchQuery,
        setSearchQuery,
        handleSearch,
        handleShowAll,
        isLoading: searchQueryResult.isFetching || byIdQuery.isFetching,
        isSearchLoading: searchQueryResult.isLoading,
        isSearchFetching: searchQueryResult.isFetching,
        idle,
        handleChange,
        getOptionLabel,
        getSelectedLabel: getSelectedLabelResolved,
    };
}
